// ============================================
// Chat Widget Component
// Floating chat button and expandable panel
// ============================================

import React, { useState, useEffect } from 'react';
import { useChat } from './ChatProvider';
import ChatPanel from './ChatPanel';

/**
 * Floating chat widget with badge and expandable panel
 */
const ChatWidget = () => {
  const {
    isOpen,
    isMinimized,
    toggleChat,
    maximizeChat,
    totalUnread,
    isConnected,
    connectionState,
    notificationPermission,
    requestNotificationPermission
  } = useChat();

  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [hasShownPrompt, setHasShownPrompt] = useState(false);

  // Show permission prompt when chat is first opened (only once per session)
  useEffect(() => {
    if (isOpen && !isMinimized && notificationPermission === 'default' && !hasShownPrompt) {
      // Delay showing the prompt slightly
      const timer = setTimeout(() => {
        setShowPermissionPrompt(true);
        setHasShownPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMinimized, notificationPermission, hasShownPrompt]);

  const handleEnableNotifications = async () => {
    await requestNotificationPermission();
    setShowPermissionPrompt(false);
  };

  const handleDismissPrompt = () => {
    setShowPermissionPrompt(false);
  };

  // Connection status indicator color
  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500 animate-pulse';
      case 'disconnected':
      case 'suspended':
        return 'bg-gray-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <>
      {/* Notification Permission Prompt - positioned near top of chat panel */}
      {showPermissionPrompt && isOpen && !isMinimized && (
        <div className="fixed bottom-[480px] right-5 w-[300px] bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-[999998]">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900">Enable Notifications?</h4>
              <p className="text-xs text-gray-500 mt-1">Get notified when you receive new messages, even when the chat is closed.</p>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={handleEnableNotifications}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
                >
                  Enable
                </button>
                <button
                  onClick={handleDismissPrompt}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>
            <button
              onClick={handleDismissPrompt}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Chat Panel */}
      {isOpen && !isMinimized && <ChatPanel />}

      {/* Minimized Bar */}
      {isOpen && isMinimized && (
        <div
          onClick={maximizeChat}
          className="fixed bottom-28 right-5 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg cursor-pointer hover:bg-blue-700 transition-colors flex items-center space-x-2 z-[999996]"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="font-medium">NACME Connect</span>
          {totalUnread > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </div>
      )}

      {/* Floating Button (when closed) */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-28 right-5 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 z-[999996]"
          title="Open Chat"
        >
          {/* Chat icon */}
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>

          {/* Connection status dot */}
          <span className={`absolute top-0 right-0 w-3 h-3 ${getStatusColor()} rounded-full border-2 border-white`}></span>

          {/* Unread badge */}
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      )}
    </>
  );
};

export default ChatWidget;
